import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Invoice PDF Document Component
 * Professional invoice generation using @react-pdf/renderer
 * Auto-generated when memos are approved
 */
import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
// PDF Styles - Professional Invoice Design
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 11,
        fontFamily: 'Times-Roman',
        color: '#000',
        backgroundColor: '#fff'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 30,
        paddingBottom: 15,
        borderBottom: '3px solid #000'
    },
    companySection: {
        width: '50%'
    },
    companyName: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#000'
    },
    companyDetails: {
        fontSize: 10,
        color: '#666',
        lineHeight: 1.5
    },
    invoiceSection: {
        width: '40%',
        textAlign: 'right'
    },
    invoiceTitle: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#000',
        marginBottom: 10
    },
    invoiceNumber: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 4
    },
    invoiceDate: {
        fontSize: 10,
        color: '#666'
    },
    billToSection: {
        marginBottom: 30
    },
    billToLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#000'
    },
    billToDetails: {
        fontSize: 11,
        lineHeight: 1.6,
        color: '#333'
    },
    table: {
        marginTop: 20,
        marginBottom: 20
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#000',
        padding: 10,
        fontWeight: 'bold'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        borderLeftWidth: 1,
        borderLeftColor: '#000',
        borderRightWidth: 1,
        borderRightColor: '#000',
        padding: 10,
        minHeight: 40
    },
    tableCol1: {
        width: '50%',
        paddingRight: 5
    },
    tableCol2: {
        width: '15%',
        textAlign: 'center'
    },
    tableCol3: {
        width: '17.5%',
        textAlign: 'right'
    },
    tableCol4: {
        width: '17.5%',
        textAlign: 'right'
    },
    totalsSection: {
        marginTop: 20,
        alignItems: 'flex-end'
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 250,
        marginBottom: 8,
        paddingVertical: 4
    },
    totalLabel: {
        fontSize: 11,
        color: '#333'
    },
    totalValue: {
        fontSize: 11,
        fontWeight: 'bold',
        textAlign: 'right'
    },
    grandTotalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 250,
        marginTop: 10,
        paddingTop: 10,
        borderTop: '2px solid #000',
        paddingVertical: 8,
        backgroundColor: '#f9fafb'
    },
    grandTotalLabel: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#000'
    },
    grandTotalValue: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#000',
        textAlign: 'right'
    },
    paymentSection: {
        marginTop: 30,
        padding: 15,
        backgroundColor: '#f9fafb',
        borderWidth: 1,
        borderColor: '#ddd'
    },
    paymentTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 10,
        color: '#000'
    },
    paymentDetails: {
        fontSize: 10,
        lineHeight: 1.6,
        color: '#333'
    },
    notesSection: {
        marginTop: 20,
        padding: 15,
        backgroundColor: '#fffbeb',
        borderWidth: 1,
        borderColor: '#fbbf24'
    },
    notesTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 6,
        color: '#000'
    },
    notesText: {
        fontSize: 10,
        lineHeight: 1.5,
        color: '#333'
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        paddingTop: 15,
        borderTop: '1px solid #ddd',
        fontSize: 9,
        color: '#666',
        textAlign: 'center'
    }
});
export const InvoicePDFDocument = ({ invoice }) => {
    // Format currency
    const formatCurrency = (amount) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    // Determine if overdue
    const isOverdue = new Date() > new Date(invoice.dueDate) && invoice.status === 'issued';
    const statusColor = isOverdue ? '#ef4444' : invoice.status === 'paid' ? '#10b981' : '#3b82f6';
    return (_jsx(Document, { children: _jsxs(Page, { size: "LETTER", style: styles.page, children: [_jsxs(View, { style: styles.header, children: [_jsxs(View, { style: styles.companySection, children: [_jsx(Text, { style: styles.companyName, children: invoice.companyName }), _jsxs(Text, { style: styles.companyDetails, children: [invoice.companyAddress.split('\n').map((line, i) => (_jsxs(React.Fragment, { children: [line, '\n'] }, i))), "Phone: ", invoice.companyPhone, '\n', "Email: ", invoice.companyEmail, invoice.companyTaxId && `\nTax ID: ${invoice.companyTaxId}`] })] }), _jsxs(View, { style: styles.invoiceSection, children: [_jsx(Text, { style: styles.invoiceTitle, children: "INVOICE" }), _jsx(Text, { style: styles.invoiceNumber, children: invoice.invoiceNumber }), _jsxs(Text, { style: styles.invoiceDate, children: ["Issue Date: ", formatDate(invoice.issueDate)] }), _jsxs(Text, { style: styles.invoiceDate, children: ["Due Date: ", formatDate(invoice.dueDate)] }), _jsxs(Text, { style: { ...styles.invoiceDate, color: statusColor, fontWeight: 'bold', marginTop: 5 }, children: ["Status: ", invoice.status.toUpperCase()] })] })] }), _jsxs(View, { style: styles.billToSection, children: [_jsx(Text, { style: styles.billToLabel, children: "BILL TO:" }), _jsxs(Text, { style: styles.billToDetails, children: [invoice.customerBusinessName || invoice.customerName, '\n', invoice.customerName !== invoice.customerBusinessName && invoice.customerBusinessName && (_jsxs(_Fragment, { children: [invoice.customerName, '\n'] })), invoice.customerAddress && _jsxs(_Fragment, { children: [invoice.customerAddress, '\n'] }), invoice.customerEmail, '\n', invoice.customerPhone && _jsxs(_Fragment, { children: [invoice.customerPhone, '\n'] }), invoice.customerTaxId && _jsxs(_Fragment, { children: ["Tax ID: ", invoice.customerTaxId] })] })] }), _jsxs(View, { style: styles.table, children: [_jsxs(View, { style: styles.tableHeader, children: [_jsx(Text, { style: styles.tableCol1, children: "Description" }), _jsx(Text, { style: styles.tableCol2, children: "Qty" }), _jsx(Text, { style: styles.tableCol3, children: "Unit Price" }), _jsx(Text, { style: styles.tableCol4, children: "Total" })] }), invoice.lineItems.map((item, index) => (_jsxs(View, { style: styles.tableRow, children: [_jsxs(Text, { style: styles.tableCol1, children: [item.description, item.stockNumber && `\nStock #: ${item.stockNumber}`, item.certificateNumber && `\nCert #: ${item.certificateNumber}`] }), _jsx(Text, { style: styles.tableCol2, children: item.quantity }), _jsx(Text, { style: styles.tableCol3, children: formatCurrency(item.unitPrice) }), _jsx(Text, { style: styles.tableCol4, children: formatCurrency(item.total) })] }, item.id))), _jsx(View, { style: { borderBottomWidth: 1, borderBottomColor: '#000' } })] }), _jsxs(View, { style: styles.totalsSection, children: [_jsxs(View, { style: styles.totalRow, children: [_jsx(Text, { style: styles.totalLabel, children: "Subtotal:" }), _jsx(Text, { style: styles.totalValue, children: formatCurrency(invoice.subtotal) })] }), invoice.discountAmount && invoice.discountAmount > 0 && (_jsxs(View, { style: styles.totalRow, children: [_jsxs(Text, { style: styles.totalLabel, children: ["Discount", invoice.discountReason && ` (${invoice.discountReason})`, ":"] }), _jsxs(Text, { style: { ...styles.totalValue, color: '#10b981' }, children: ["-", formatCurrency(invoice.discountAmount)] })] })), invoice.taxAmount && invoice.taxAmount > 0 && (_jsxs(View, { style: styles.totalRow, children: [_jsxs(Text, { style: styles.totalLabel, children: ["Tax", invoice.taxRate && ` (${(invoice.taxRate * 100).toFixed(2)}%)`, ":"] }), _jsx(Text, { style: styles.totalValue, children: formatCurrency(invoice.taxAmount) })] })), _jsxs(View, { style: styles.grandTotalRow, children: [_jsx(Text, { style: styles.grandTotalLabel, children: "TOTAL DUE:" }), _jsx(Text, { style: styles.grandTotalValue, children: formatCurrency(invoice.total) })] })] }), invoice.bankingInfo && (_jsxs(View, { style: styles.paymentSection, children: [_jsx(Text, { style: styles.paymentTitle, children: "PAYMENT INFORMATION" }), _jsxs(Text, { style: styles.paymentDetails, children: ["Payment Terms: ", invoice.paymentTerms, '\n', '\n', "Wire Transfer Details:", '\n', "Bank Name: ", invoice.bankingInfo.bankName, '\n', "Account Number: ", invoice.bankingInfo.accountNumber, '\n', "Routing Number: ", invoice.bankingInfo.routingNumber, '\n', "Account Type: ", invoice.bankingInfo.accountType, invoice.bankingInfo.swiftCode && `\nSWIFT Code: ${invoice.bankingInfo.swiftCode}`] })] })), invoice.notes && (_jsxs(View, { style: styles.notesSection, children: [_jsx(Text, { style: styles.notesTitle, children: "NOTES:" }), _jsx(Text, { style: styles.notesText, children: invoice.notes })] })), _jsx(View, { style: styles.footer, children: _jsxs(Text, { children: [invoice.terms || 'All sales are final. Late payments subject to interest charges.', '\n', "For questions about this invoice, please contact ", invoice.companyEmail] }) })] }) }));
};
